// UM cliente supabase só no app inteiro (o do useAuth). Ter DOIS GoTrueClient
// no mesmo storage corrompia o refresh token em sessão longa: um renovava, o
// outro ficava com o token velho → "Invalid Refresh Token" e login em loop.
import { supabase } from "../hooks/useAuth";

export { supabase };

// TTL de 24h para signed URLs de fotos (suficiente pra uma sessão de trabalho).
const FOTO_SIGNED_TTL = 60 * 60 * 24;

/**
 * Converte um path de storage OU uma URL pública legada em signed URL.
 * Retrocompatível: URLs antigas do formato /object/public/obra-fotos/{path}
 * têm o prefixo removido e a signed URL é gerada normalmente.
 */
export async function getFotoSignedUrl(pathOrUrl: string, bucket = "obra-fotos"): Promise<string> {
  let storagePath = pathOrUrl;

  // URL pública legada → extrai o path
  const PUBLIC_PREFIX = `/storage/v1/object/public/${bucket}/`;
  const idx = pathOrUrl.indexOf(PUBLIC_PREFIX);
  if (idx !== -1) {
    storagePath = pathOrUrl.slice(idx + PUBLIC_PREFIX.length);
  } else if (pathOrUrl.startsWith("https://")) {
    // URL desconhecida (ex: já signed ou externa) → usa direto
    return pathOrUrl;
  }

  const { data, error } = await supabase.storage
    .from(bucket)
    .createSignedUrl(storagePath, FOTO_SIGNED_TTL);
  if (error || !data) return pathOrUrl; // fallback: retorna o que tiver
  return data.signedUrl;
}

/**
 * Faz upload de uma foto pro bucket obra-fotos (agora privado).
 * Retorna:
 *  - path: caminho curto pra guardar no banco
 *  - url: signed URL válida por 24h pra exibição imediata
 */
export async function uploadFoto(
  file: File,
  prefix: string,
  bucket = "obra-fotos"
): Promise<{ url: string; path: string }> {
  const ext = file.name.split(".").pop()?.toLowerCase() ?? "jpg";
  const path = `${prefix}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
  const { error } = await supabase.storage.from(bucket).upload(path, file, {
    cacheControl: "3600",
    upsert: false,
  });
  if (error) throw new Error(error.message);
  const { data } = await supabase.storage
    .from(bucket)
    .createSignedUrl(path, FOTO_SIGNED_TTL);
  return { url: data?.signedUrl ?? path, path };
}

export async function deletarFoto(path: string, bucket = "obra-fotos") {
  const { error } = await supabase.storage.from(bucket).remove([path]);
  if (error) throw new Error(error.message);
}
